import classNames from 'classnames';
import React, {
    MouseEvent,
    MouseEventHandler,
    ReactElement,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    CSSTransition,
    TransitionGroup,
} from 'react-transition-group';

import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';

import { Dot } from './Dot';
import { DOT_PAGINATION_VARIANTS } from './DOT_PAGINATION_VARIANTS';
import styles from './DotPagination.module.css';
import { DotPaginationContext, IDotPaginationContext } from './DotPaginationContext';
import { getDefaultNavState } from './navState/getDefaultNavState';
import {
    getNavState,
    INavDot,
    INavState,
} from './navState/navState';

export type IDotPaginationProps = IQATestableProps & IStyleableProps & {
    variant?: DOT_PAGINATION_VARIANTS;
    totalPages: number;
    currentPage: number;
    onChange: (page: number) => void;
};

export function OzDotPagination (props: IDotPaginationProps): ReactElement {
    const {
        'data-testid': dataTestId = '',
        className = '',
        variant = DOT_PAGINATION_VARIANTS.LIGHT,
        totalPages,
        currentPage,
        onChange,
    }: IDotPaginationProps = props;

    const [navState, setNavState]: [INavState, ISetState<INavState>] = useState<INavState>(getDefaultNavState(totalPages));

    function updateNavState (selectedPage: number): void {
        setNavState((prevState) => {
            const newNavState: INavState = getNavState(prevState, selectedPage, totalPages);

            return {
                ...navState,
                ...newNavState,
            };
        });
    }

    const [dotRefs, setDotRefs]: [RefObject<HTMLButtonElement>[], ISetState<RefObject<HTMLButtonElement>[]>] =
        useState<RefObject<HTMLButtonElement>[]>([]);

    const dotRefsRef: RefObject<RefObject<HTMLButtonElement>[]> = useRef<RefObject<HTMLButtonElement>[]>([]);

    function addDotRef (dotRef: RefObject<HTMLButtonElement>): void {
        dotRefsRef.current?.push(dotRef);

        const existingRefs = dotRefsRef.current?.filter((ref) => {
            return !!ref.current;
        });

        setDotRefs(existingRefs || []);
    }

    const dotPaginationContext: IDotPaginationContext = {
        variant,
        totalPages,
        navState,
        addDotRef,
        'data-testid': dataTestId,
    };

    // update navState on currentPage prop change
    useEffect((): void => {
        updateNavState(currentPage);
    }, [currentPage]);

    // update focused dot/position
    useEffect((): void => {
        dotRefs.forEach((dotRef: RefObject<HTMLButtonElement>): void => {
            if (dotRef.current?.getAttribute('data-index') === navState.selectedDot + '') {
                dotRef.current?.focus();
            } else if (dotRef.current?.getAttribute('aria-current') === 'page') {
                dotRef.current?.blur();
            }
        });
    }, [navState.selectedDot]);

    function createDotClickHandler (newSelectedPage: number): MouseEventHandler<HTMLElement> | undefined {
        return (_e: MouseEvent<HTMLElement>): void => {
            updateNavState(newSelectedPage);
            onChange(newSelectedPage);
        };
    }

    const navClassNames: string = classNames({
        [styles.host]: true,
        [styles.staticNav]: navState.static,
        [styles.movableNav]: !navState.static,
        className: !!className,
    });

    return (
        <DotPaginationContext.Provider value={dotPaginationContext}>
            <nav data-testid={dataTestId} aria-label="pagination" className={navClassNames}>
                <TransitionGroup component={null}>
                    {
                        navState.dots.map((navDot: INavDot, index: number): ReactElement => {
                            const dotNumber: number = index + 1;

                            const dotClassNames: string = classNames({
                                'relative mr-2': navState.static,
                                'absolute m-0': !navState.static,
                                [styles.dot]: true,
                                [styles.dotTransition]: navState.inNavSelection,
                                [styles.sizeL]: navDot.size === 'sizeL',
                                [styles.sizeM]: navDot.size === 'sizeM',
                                [styles.sizeS]: navDot.size === 'sizeS',
                                [styles.position1]: !navState.static && navDot.position === 1,
                                [styles.position2]: !navState.static && navDot.position === 2,
                                [styles.position3]: !navState.static && navDot.position === 3,
                                [styles.position4]: !navState.static && navDot.position === 4,
                                [styles.position5]: !navState.static && navDot.position === 5,
                                [styles.position6]: !navState.static && navDot.position === 6,
                                [styles.position7]: !navState.static && navDot.position === 7,
                                [styles.position8]: !navState.static && navDot.position === 8,
                                [styles.position9]: !navState.static && navDot.position === 9,
                            });

                            return (
                                <CSSTransition
                                    key={navDot.page}
                                    timeout={300}  // should match the CSS transition speed
                                    classNames={{
                                        enter: styles[navDot.transition.enterStart],
                                        enterActive: styles[navDot.transition.enterEnd],
                                        enterDone: styles[navDot.transition.enterEnd],
                                        exitActive: styles[navDot.transition.exit],
                                    }}
                                >
                                    <Dot
                                        data-testid={`${dataTestId}--dot-${dotNumber}`}
                                        className={dotClassNames}
                                        key={navDot.page}
                                        index={dotNumber}
                                        isCurrent={dotNumber === navState.selectedDot}
                                        aria-label={`Go to page ${navDot.page}`}
                                        onClick={createDotClickHandler(navDot.page)}
                                    />
                                </CSSTransition>
                            );
                        })
                    }
                </TransitionGroup>
            </nav>
        </DotPaginationContext.Provider>
    );
}
